import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button, Badge } from '../../../components/ui';
import { Accordion, AccordionItem } from '../../../components/ui/Accordion';
import { Column } from '../../../components/DataTable/DataTable';
import { useClientSummary, ClientSummary } from '../useData';
import { formatDate } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import type { ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatCurrency } from '../../../utils/formatCurrency';
import api from '../../../services/api';
import { getStatusBadges } from '../config';
import { getColumns as getClientInvoicesColumns } from '../../client-invoices/config';
import { getColumns as getAppointmentsColumns } from '../../crm-appointments/config';
import { getColumns as getCallsColumns } from '../../crm-calls/config';
import { getColumns as getRemindersColumns } from '../../reminders/config';
import { getColumns as getClientServicesColumns } from '../../client-services/config';
import { getColumns as getTasksColumns } from '../../crm-tasks/config';
import { getColumns as getNotesColumns } from '../../crm-notes/config';
import { getColumns as getTodosColumns } from '../../crm-todos/config';
import { getColumns as getSupportTicketsColumns } from '../../crm-support-ticket/config';
import { getColumns as getContactsColumns } from '../../client-contacts/config';
import { getColumns as getPackagesColumns } from '../../crm-packages/config';
import { getColumns as getSupportAgreementsColumns } from '../../crm-support-agreements/config';
import { getColumns as getProjectsColumns } from '../../crm-projects/config';
import { getColumns as getLeadsColumns } from '../../crm-leads/config';
import { getColumns as getOffersColumns } from '../../offers/config';

export interface ClientSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number | null;
}

/** Row for read-only detail: label + value. Renders nothing when value is empty. */
function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  const { colors } = useTheme();
  const isEmpty = value === undefined || value === null || value === '';
  if (isEmpty) return null;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-4 gap-y-1 py-1.5 text-sm">
      <span className={cn('font-medium', colors.textSecondary)}>{label}</span>
      <span className={colors.text}>{String(value)}</span>
    </div>
  );
}

type ClientSummaryCounts = import('../useData').ClientSummaryCounts;

/** Column definition for the records table */
interface RecordColumn {
  key: string;
  label: string;
  getValue: (record: Record<string, unknown>) => string | number | null | undefined;
  /** When set, cell is rendered as a colored status badge */
  getStatusColor?: (record: Record<string, unknown>) => string | null | undefined;
}

/** Group for related modules (display grouping in right panel) */
export type RelatedModuleGroup = 'CRM' | 'Documents' | 'Finance' | 'Other';

/** Related module config: fetchUrl returns API path for records by client; columns for table display */
interface RelatedModuleConfig {
  key: string;
  label: string;
  path: string;
  countKey: keyof ClientSummaryCounts;
  group?: RelatedModuleGroup;
  fetchUrl?: (clientId: number) => string;
  getRecordId?: (record: Record<string, unknown>) => number;
  getRecordLabel?: (record: Record<string, unknown>) => string;
  detailPath?: (id: number, clientId?: number) => string;
  /** If false, no Edit link is shown (e.g. vault folders, emails). Default true. */
  hasEdit?: boolean;
  /** Extra columns for the records table (first column is always the link/title). Ignored when getColumnsFromModule is set. */
  columns?: RecordColumn[];
  /** When set, table uses the same columns as the module list (getColumns from that module). */
  getColumnsFromModule?: (
    utils: { formatDate: (d?: string) => string; formatCurrency: (a?: number | null) => string },
    colors?: ThemeColors,
    listContext?: { mutate?: () => void }
  ) => Column<Record<string, unknown>>[];
}

/** Get display string from a relation object (e.g. { as_descr }, { pit_descr }, { pct_descr }, { tt_descr }) */
function descrFrom(rel: unknown): string | null {
  if (rel == null) return null;
  const o = rel as Record<string, unknown>;
  return (o.as_descr ?? o.pit_descr ?? o.pct_descr ?? o.pps_descr ?? o.pst_descr ?? o.pd_descr ?? o.tt_descr) as string ?? null;
}

/** Get status color from a relation object (as_color, pts_color, pst_color, tt_color, etc.) */
function colorFrom(rel: unknown): string | null {
  if (rel == null) return null;
  const o = rel as Record<string, unknown>;
  const raw = o.as_color ?? o.pts_color ?? o.pst_color ?? o.tt_color ?? o.pd_color;
  return typeof raw === 'string' ? raw : null;
}

/** Get nested value from record by key (e.g. 'client.clientName'). */
function getNestedValue(record: Record<string, unknown>, key: string): unknown {
  if (!key || !record) return undefined;
  const parts = key.split('.');
  let current: unknown = record;
  for (const p of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[p];
  }
  return current;
}

const RELATED_MODULES: RelatedModuleConfig[] = [
  {
    key: 'appointments', label: 'Appointments', path: '/appointments', countKey: 'clientAppointments', group: 'CRM',
    fetchUrl: (c) => `/crm-clients-appointments/all_of/${c}`, getRecordId: (r) => (r.clientAppId as number), getRecordLabel: (r) => (r.clientAppName as string) || `#${r.clientAppId}`, detailPath: (id) => `/appointments/${id}`,
    getColumnsFromModule: (utils, colors) => getAppointmentsColumns(utils.formatDate, undefined, colors) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'date', label: 'Date', getValue: (r) => r.clientAppScheduleDateTime ? formatDate(String(r.clientAppScheduleDateTime)) : null },
      { key: 'status', label: 'Status', getValue: (r) => descrFrom(r.clientAppStatusDescr), getStatusColor: (r) => colorFrom(r.clientAppStatusDescr) },
      { key: 'assigned', label: 'Assigned to', getValue: (r) => (r.assignedToUser as { userFirstName?: string; userLastName?: string }) ? [(r.assignedToUser as any).userFirstName, (r.assignedToUser as any).userLastName].filter(Boolean).join(' ') : null },
    ],
  },
  {
    key: 'calls', label: 'Calls', path: '/calls', countKey: 'clientCalls', group: 'CRM',
    fetchUrl: (c) => `/crm-clients-calls/all_of/${c}`, getRecordId: (r) => (r.clientCallId as number), getRecordLabel: (r) => (r.clientCallDescr as string) || (r.clientCallDate ? formatDate(String(r.clientCallDate)) : `#${r.clientCallId}`), detailPath: (id) => `/calls/${id}`,
    getColumnsFromModule: (utils) => getCallsColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'date', label: 'Date', getValue: (r) => r.clientCallDate ? formatDate(String(r.clientCallDate)) : null },
      { key: 'type', label: 'Type', getValue: (r) => descrFrom(r.clientCallType) },
      { key: 'notes', label: 'Notes', getValue: (r) => (r.clientCallNotes as string)?.slice(0, 50) ?? null },
    ],
  },
  {
    key: 'reminders', label: 'Reminders', path: '/reminders', countKey: 'clientReminders', group: 'CRM',
    fetchUrl: (c) => `/crm-clients-reminders/all_of/${c}`, getRecordId: (r) => (r.clientReminderId as number), getRecordLabel: (r) => (r.clientReminderDescr as string) || (r.clientReminderDateTime ? formatDate(String(r.clientReminderDateTime)) : `#${r.clientReminderId}`), detailPath: (id) => `/reminders/${id}`,
    getColumnsFromModule: (utils) => getRemindersColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'date', label: 'Due', getValue: (r) => r.clientReminderDateTime ? formatDate(String(r.clientReminderDateTime)) : null },
      { key: 'done', label: 'Done', getValue: (r) => r.clientReminderIsDone ? 'Yes' : 'No' },
      { key: 'assigned', label: 'Assigned to', getValue: (r) => (r.assignedToUser as { userFirstName?: string })?.userFirstName ?? null },
    ],
  },
  {
    key: 'invoices', label: 'Invoices', path: '/client-invoices', countKey: 'clientInvoices', group: 'Finance',
    fetchUrl: (c) => `/crm-clients-invoices/all_of/${c}`, getRecordId: (r) => (r.invoiceId as number), getRecordLabel: (r) => (r.InvoiceNo as string) || `#${r.invoiceId}`, detailPath: (id) => `/client-invoices/${id}`,
    getColumnsFromModule: (utils, colors, listContext) => getClientInvoicesColumns(utils.formatDate, undefined, colors, listContext) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'date', label: 'Date', getValue: (r) => r.InvoiceDate ? formatDate(String(r.InvoiceDate)) : null },
      { key: 'due', label: 'Due', getValue: (r) => r.invoiceDue ? formatDate(String(r.invoiceDue)) : null },
      { key: 'total', label: 'Total', getValue: (r) => r.invoiceAmount != null ? Number(r.invoiceAmount) : null },
      { key: 'type', label: 'Type', getValue: (r) => descrFrom(r.invoiceTypeDescr) },
    ],
  },
  {
    key: 'client-services', label: 'Client services', path: '/client-services', countKey: 'clientServices', group: 'CRM',
    fetchUrl: (c) => `/crm-clients-services/all_of/${c}`, getRecordId: (r) => (r.clientServicetId as number), getRecordLabel: (r) => (r.serviceDescr as string) || descrFrom(r.serviceTypeDescr) || `#${r.clientServicetId}`, detailPath: (id) => `/client-services/${id}`,
    getColumnsFromModule: (utils, colors) => getClientServicesColumns(utils.formatDate, undefined, colors, undefined) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'type', label: 'Type', getValue: (r) => descrFrom(r.serviceTypeDescr) },
      { key: 'category', label: 'Category', getValue: (r) => (r.serviceCategoryDescr as { psc_descr?: string })?.psc_descr ?? null },
      { key: 'start', label: 'Start', getValue: (r) => r.serviceStartDate ? formatDate(String(r.serviceStartDate)) : null },
      { key: 'end', label: 'End', getValue: (r) => r.serviceEndDate ? formatDate(String(r.serviceEndDate)) : null },
      { key: 'active', label: 'Active', getValue: (r) => r.serviceActive != null ? (r.serviceActive ? 'Yes' : 'No') : null },
    ],
  },
  {
    key: 'tasks', label: 'Tasks', path: '/crm_tasks', countKey: 'clientTasks', group: 'CRM',
    fetchUrl: (c) => `/crm-tasks/all_of/${c}`, getRecordId: (r) => (r.taskId as number), getRecordLabel: (r) => (r.taskDescription as string) || `#${r.taskId}`, detailPath: (id) => `/crm_tasks/${id}`,
    getColumnsFromModule: (utils, colors) => getTasksColumns(utils.formatDate, colors) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'date', label: 'Date', getValue: (r) => r.taskDate ? formatDate(String(r.taskDate)) : null },
      { key: 'status', label: 'Status', getValue: (r) => descrFrom(r.taskStatus), getStatusColor: (r) => colorFrom(r.taskStatus) },
      { key: 'assigned', label: 'Assigned to', getValue: (r) => (r.taskAssignedToUser as { userFirstName?: string; userLastName?: string }) ? [(r.taskAssignedToUser as any).userFirstName, (r.taskAssignedToUser as any).userLastName].filter(Boolean).join(' ') : null },
    ],
  },
  {
    key: 'notes', label: 'Notes', path: '/crm_notes', countKey: 'clientGeneralNotes', group: 'CRM',
    fetchUrl: (c) => `/crm-notes/all_of/${c}`, getRecordId: (r) => (r.noteId as number), getRecordLabel: (r) => (r.noteDescr as string)?.slice(0, 60) || `#${r.noteId}`, detailPath: (id) => `/crm_notes/${id}`,
    getColumnsFromModule: (utils) => getNotesColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'created', label: 'Created', getValue: (r) => r.noteCreatedAt ? formatDate(String(r.noteCreatedAt)) : null },
      { key: 'archived', label: 'Archived', getValue: (r) => r.noteArchived ? 'Yes' : 'No' },
    ],
  },
  {
    key: 'todos', label: 'Todos', path: '/todos', countKey: 'clientTodos', group: 'CRM',
    fetchUrl: (c) => `/crm-todos/all_of/${c}`, getRecordId: (r) => (r.todoId as number), getRecordLabel: (r) => (r.todoDescr as string)?.slice(0, 60) || `#${r.todoId}`, detailPath: (id) => `/todos/${id}`,
    getColumnsFromModule: (utils) => getTodosColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'due', label: 'Due date', getValue: (r) => r.todoDueDate ? formatDate(String(r.todoDueDate)) : null },
      { key: 'completed', label: 'Completed', getValue: (r) => r.todoCompleted ? 'Yes' : 'No' },
      { key: 'type', label: 'Type', getValue: (r) => descrFrom(r.todoType) },
    ],
  },
  {
    key: 'support-tickets', label: 'Support tickets', path: '/support-ticket', countKey: 'supportTickets', group: 'CRM',
    fetchUrl: (c) => `/support-ticket/all_of/${c}`, getRecordId: (r) => (r.id as number), getRecordLabel: (r) => (r.title as string) || (r.reference as string) || `#${r.id}`, detailPath: (id) => `/support-ticket/${id}`,
    getColumnsFromModule: (utils) => getSupportTicketsColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'reference', label: 'Reference', getValue: (r) => (r.reference as string) ?? null },
      { key: 'status', label: 'Status', getValue: (r) => (r.statusDescr as { pts_descr?: string })?.pts_descr ?? null, getStatusColor: (r) => (r.statusDescr as { pts_color?: string })?.pts_color ?? null },
      { key: 'priority', label: 'Priority', getValue: (r) => (r.priorityDescr as { p_descr?: string })?.p_descr ?? null },
      { key: 'created', label: 'Created', getValue: (r) => r.createdAt ? formatDate(String(r.createdAt)) : null },
      { key: 'assigned', label: 'Assigned', getValue: (r) => (r.assignedTo as { userDisplayName?: string; userFirstName?: string })?.userDisplayName ?? (r.assignedTo as { userFirstName?: string })?.userFirstName ?? null },
    ],
  },
  {
    key: 'contacts', label: 'Contacts', path: '/client-contacts', countKey: 'crm_ClientsContacts', group: 'CRM',
    fetchUrl: (c) => `/client-contacts/all_of/${c}`, getRecordId: (r) => (r.clientContactId as number), getRecordLabel: (r) => [r.clientContactFirstName, r.clientContactLastName].filter(Boolean).join(' ') || (r.clientContactEmail as string) || `#${r.clientContactId}`, detailPath: (id) => `/client-contacts/${id}`,
    getColumnsFromModule: (utils) => getContactsColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'email', label: 'Email', getValue: (r) => (r.clientContactEmail as string) ?? null },
      { key: 'phone', label: 'Phone', getValue: (r) => (r.clientContactPhone as string) ?? null },
      { key: 'position', label: 'Position', getValue: (r) => (r.clientContactPosition as string) ?? null },
    ],
  },
  {
    key: 'packages', label: 'Packages', path: '/packages', countKey: 'crm_packages', group: 'CRM',
    fetchUrl: (c) => `/crm-packages/all_of/${c}`, getRecordId: (r) => (r.id as number), getRecordLabel: (r) => (r.packageDescr as string) || descrFrom(r.packageType) || `#${r.id}`, detailPath: (id) => `/packages/${id}`,
    getColumnsFromModule: (utils, colors) => getPackagesColumns(utils.formatDate, colors) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'type', label: 'Type', getValue: (r) => descrFrom(r.packageType) },
      { key: 'category', label: 'Category', getValue: (r) => (r.packageCategory as { psc_descr?: string })?.psc_descr ?? null },
      { key: 'start', label: 'Start', getValue: (r) => r.packageStartDate ? formatDate(String(r.packageStartDate)) : null },
      { key: 'end', label: 'End', getValue: (r) => r.packageEndDate ? formatDate(String(r.packageEndDate)) : null },
      { key: 'minutes', label: 'Minutes', getValue: (r) => r.packageMinutes != null ? Number(r.packageMinutes) : null },
      { key: 'remaining', label: 'Remaining', getValue: (r) => (r as { remainingMinutes?: number }).remainingMinutes != null ? Number((r as { remainingMinutes: number }).remainingMinutes) : null },
    ],
  },
  {
    key: 'support-agreements', label: 'Support agreements', path: '/support-agreements', countKey: 'supportAgreements', group: 'Documents',
    fetchUrl: (c) => `/crm-support-agreements/client/${c}`, getRecordId: (r) => (r.id as number), getRecordLabel: (r) => (r.description as string) || `#${r.id}`, detailPath: (id) => `/support-agreements/${id}`,
    getColumnsFromModule: (utils) => getSupportAgreementsColumns(utils.formatDate, utils.formatCurrency) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'type', label: 'Type', getValue: (r) => descrFrom(r.agreementTypeDescr) },
      { key: 'start', label: 'Start', getValue: (r) => r.startDate ? formatDate(String(r.startDate)) : null },
      { key: 'end', label: 'End', getValue: (r) => r.endDate ? formatDate(String(r.endDate)) : null },
      { key: 'active', label: 'Active', getValue: (r) => r.isActive != null ? (r.isActive ? 'Yes' : 'No') : null },
    ],
  },
  {
    key: 'projects', label: 'Projects', path: '/projects', countKey: 'projects', group: 'CRM',
    fetchUrl: (c) => `/crm-projects/client/${c}`, getRecordId: (r) => (r.id as number), getRecordLabel: (r) => (r.projectName as string) || `#${r.id}`, detailPath: (id) => `/projects/${id}`,
    getColumnsFromModule: (utils) => getProjectsColumns(utils.formatDate) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'status', label: 'Status', getValue: (r) => descrFrom(r.projectStatus), getStatusColor: (r) => colorFrom(r.projectStatus) },
      { key: 'start', label: 'Start', getValue: (r) => r.projectStartDate ? formatDate(String(r.projectStartDate)) : null },
      { key: 'end', label: 'End', getValue: (r) => r.projectEndDate ? formatDate(String(r.projectEndDate)) : null },
      { key: 'budget', label: 'Budget', getValue: (r) => r.projectBudget != null ? Number(r.projectBudget) : null },
    ],
  },
  {
    key: 'leads', label: 'Leads', path: '/leads', countKey: 'crm_Leads', group: 'CRM',
    fetchUrl: (c) => `/crm-leads/all_of/${c}`, getRecordId: (r) => (r.leadId as number), getRecordLabel: (r) => (r.leadDescr as string) || descrFrom(r.leadStatusDescr) || `#${r.leadId}`, detailPath: (id) => `/leads/${id}`,
    getColumnsFromModule: (utils, colors) => getLeadsColumns(utils.formatDate, colors) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'status', label: 'Status', getValue: (r) => descrFrom(r.leadStatusDescr), getStatusColor: (r) => colorFrom(r.leadStatusDescr) },
      { key: 'contacted', label: 'Contacted', getValue: (r) => r.leadContactedDate ? formatDate(String(r.leadContactedDate)) : null },
      { key: 'value', label: 'Value', getValue: (r) => r.leadValue != null ? Number(r.leadValue) : null },
      { key: 'assigned', label: 'Assigned', getValue: (r) => (r.leadAssignedToUser as { userDisplayName?: string; userFirstName?: string })?.userDisplayName ?? (r.leadAssignedToUser as { userFirstName?: string })?.userFirstName ?? null },
    ],
  },
  {
    key: 'vault-folders', label: 'Vault folders', path: '/client-vault', countKey: 'vaultFolders', group: 'Documents',
    fetchUrl: (c) => `/client-vault/${c}/folders`, getRecordId: (r) => (r.id as number), getRecordLabel: (r) => ((r.template as { name?: string })?.name as string) || `Folder #${r.id}`, detailPath: (id, clientId) => (clientId != null ? `/client-vault?clientId=${clientId}&folderId=${id}` : '/client-vault'),
    hasEdit: false,
    columns: [
      { key: 'records', label: 'Records', getValue: (r) => (r._count as { records?: number })?.records ?? null },
      { key: 'files', label: 'Files', getValue: (r) => (r._count as { files?: number })?.files ?? null },
    ],
  },
  {
    key: 'offers', label: 'Offers', path: '/offers', countKey: 'offers', group: 'Finance',
    fetchUrl: (c) => `/offers/all_of/${c}`, getRecordId: (r) => (r.id as number), getRecordLabel: (r) => (r.offerNumber as string) || (r.description as string) || `#${r.id}`, detailPath: (id) => `/offers/${id}`,
    getColumnsFromModule: (utils, colors, listContext) => getOffersColumns(utils.formatDate, utils.formatCurrency, colors, listContext) as unknown as Column<Record<string, unknown>>[],
    columns: [
      { key: 'status', label: 'Status', getValue: (r) => (r.status as string) ?? null },
      { key: 'date', label: 'Offer date', getValue: (r) => r.offerDate ? formatDate(String(r.offerDate)) : null },
      { key: 'total', label: 'Total', getValue: (r) => r.total != null ? Number(r.total) : null },
      { key: 'validUntil', label: 'Valid until', getValue: (r) => r.validUntil ? formatDate(String(r.validUntil)) : null },
    ],
  },
];

function toRecordList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown[] }).data)) return (raw as { data: Record<string, unknown>[] }).data;
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: unknown[] }).records)) return (raw as { records: Record<string, unknown>[] }).records;
  return [];
}

const MODULE_GROUP_ORDER: RelatedModuleGroup[] = ['CRM', 'Documents', 'Finance', 'Other'];

function RelatedModuleLinks({
  clientId,
  counts,
  singleColumn = false,
  onScrollToModule,
}: {
  clientId: number;
  counts?: ClientSummary['_count'];
  singleColumn?: boolean;
  onScrollToModule: (key: string) => void;
}) {
  const { colors } = useTheme();
  const modulesWithCount = RELATED_MODULES.filter((config) => (counts?.[config.countKey] ?? 0) > 0);
  const byGroup = MODULE_GROUP_ORDER.map((group) => ({
    group,
    configs: modulesWithCount.filter((c) => (c.group ?? 'Other') === group),
  })).filter((g) => g.configs.length > 0);

  return (
    <div className={cn('grid gap-3', singleColumn ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2')}>
      {byGroup.map(({ group, configs }) => (
        <div key={group}>
          <p className={cn('text-xs font-medium uppercase tracking-wide mb-1.5', colors.textSecondary)}>{group}</p>
          <div className="space-y-2">
            {configs.map((config) => {
              const count = counts?.[config.countKey] ?? 0;
              const listPath = config.path.startsWith('/client-vault') ? `${config.path}/${clientId}` : config.path;
              const hasFetch = !!config.fetchUrl;
              return (
                <div key={config.key} className="flex items-center gap-2">
                  {hasFetch ? (
                    <button
                      type="button"
                      onClick={() => onScrollToModule(config.key)}
                      className={cn(
                        'flex-1 flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-left transition-colors',
                        colors.border,
                        colors.surface,
                        colors.interactiveHover
                      )}
                    >
                      <span className={colors.text}>{config.label}</span>
                      <span className={cn('font-medium tabular-nums', colors.textSecondary)}>{count}</span>
                    </button>
                  ) : (
                    <Link
                      to={listPath}
                      className={cn(
                        'flex-1 flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors',
                        colors.border,
                        colors.surface,
                        colors.interactiveHover
                      )}
                    >
                      <span className={colors.text}>{config.label}</span>
                      <span className={cn('font-medium tabular-nums', colors.textSecondary)}>{count}</span>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Quick actions: new record with clientId pre-filled (uses query param where supported) */
const QUICK_ACTIONS: { label: string; path: string; pathWithClient?: (clientId: number) => string; icon: React.ReactNode }[] = [
  { label: 'New appointment', path: '/appointments/new', pathWithClient: (c) => `/appointments/new?clientId=${c}`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
  { label: 'New invoice', path: '/client-invoices/new', pathWithClient: (c) => `/client-invoices/new?clientId=${c}`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> },
  { label: 'New offer', path: '/offers/new', pathWithClient: (c) => `/offers/new?clientId=${c}`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
  { label: 'New task', path: '/crm_tasks/new', pathWithClient: (c) => `/crm_tasks/new?clientId=${c}`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg> },
  { label: 'New reminder', path: '/reminders/new', pathWithClient: (c) => `/reminders/new?clientId=${c}`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg> },
  { label: 'New support ticket', path: '/support-ticket/new', pathWithClient: (c) => `/support-ticket/new?clientId=${c}`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg> },
];

type RecordsState = { loading: boolean; error: string | null; data: Record<string, unknown>[] };

/** Shared summary body: KPIs, quick actions, accordion, sidebar, records. Used in modal and summary page. */
export function ClientSummaryBody({
  clientSummary,
  mutate,
}: {
  clientSummary: ClientSummary;
  mutate: () => void;
}) {
  const { colors } = useTheme();
  const [recordsByKey, setRecordsByKey] = useState<Record<string, RecordsState>>({});

  const loadModuleRecords = useCallback(async (config: RelatedModuleConfig, cId: number) => {
    if (!config.fetchUrl) return;
    const key = config.key;
    setRecordsByKey((prev) => ({ ...prev, [key]: { loading: true, error: null, data: [] } }));
    try {
      const url = config.fetchUrl(cId);
      const res = await api.get(url);
      const data = toRecordList(res.data);
      setRecordsByKey((prev) => ({ ...prev, [key]: { loading: false, error: null, data } }));
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to load';
      setRecordsByKey((prev) => ({ ...prev, [key]: { loading: false, error: message, data: [] } }));
    }
  }, []);

  useEffect(() => {
    if (!clientSummary?.clientId || !clientSummary._count) return;
    const toLoad = RELATED_MODULES.filter(
      (config) => config.fetchUrl && (clientSummary._count?.[config.countKey] ?? 0) > 0
    );
    toLoad.forEach((config) => loadModuleRecords(config, clientSummary.clientId));
  }, [clientSummary?.clientId, clientSummary?._count, loadModuleRecords]);

  const handleScrollToModule = useCallback((key: string) => {
    document.getElementById(`records-${key}`)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div>
      {/* Horizontal quick actions – icon buttons only */}
      <div className={cn('flex flex-wrap items-center gap-2 mb-4', colors.border, 'pb-3 border-b')}>
              {QUICK_ACTIONS.map((action) => (
                <Link
                  key={action.path}
                  to={action.pathWithClient ? action.pathWithClient(clientSummary.clientId) : action.path}
                  className={cn(
                    'p-2.5 rounded-lg border transition-colors',
                    colors.border,
                    colors.surface,
                    colors.text,
                    colors.interactiveHover
                  )}
                  title={action.label}
                  aria-label={action.label}
                >
                  {action.icon}
                </Link>
              ))}
            </div>

            {/* KPIs – card grid for quick scanning */}
            <div className="mb-6">
              <h3 className={cn('text-xs font-semibold uppercase tracking-wider mb-3', colors.textSecondary)}>
                Key metrics
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {clientSummary.kpis && (
                  <>
                    <div className={cn('rounded-xl border p-3 min-w-0', colors.border, colors.surface)}>
                      <div className="flex items-center gap-2">
                        <span className={cn('rounded-lg p-1.5', colors.surfaceHover)} aria-hidden>
                          <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </span>
                        <p className={cn('text-xs font-medium truncate', colors.textSecondary)}>Total invoiced</p>
                      </div>
                      <p className={cn('text-lg font-semibold mt-0.5 truncate', colors.text)}>
                        {formatCurrency(clientSummary.kpis.totalInvoiced)}
                      </p>
                    </div>
                    <div className={cn('rounded-xl border p-3 min-w-0', colors.border, colors.surface)}>
                      <div className="flex items-center gap-2">
                        <span className={cn('rounded-lg p-1.5', clientSummary.kpis.unpaidAmount > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : colors.surfaceHover)} aria-hidden>
                          <svg className={cn('w-4 h-4', clientSummary.kpis.unpaidAmount > 0 ? 'text-amber-600 dark:text-amber-400' : colors.textSecondary)} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </span>
                        <p className={cn('text-xs font-medium truncate', colors.textSecondary)}>Unpaid</p>
                      </div>
                      <p className={cn('text-lg font-semibold mt-0.5 truncate', clientSummary.kpis.unpaidAmount > 0 ? 'text-amber-600 dark:text-amber-400' : colors.text)}>
                        {formatCurrency(clientSummary.kpis.unpaidAmount)}
                      </p>
                    </div>
                    <div className={cn(
                      'rounded-xl border p-3 min-w-0',
                      clientSummary.kpis.overdueInvoicesCount > 0
                        ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                        : cn(colors.border, colors.surface)
                    )}>
                      <div className="flex items-center gap-2">
                        <span className={cn('rounded-lg p-1.5', clientSummary.kpis.overdueInvoicesCount > 0 ? 'bg-red-100 dark:bg-red-900/30' : colors.surfaceHover)} aria-hidden>
                          <svg className={cn('w-4 h-4', clientSummary.kpis.overdueInvoicesCount > 0 ? 'text-red-600 dark:text-red-400' : colors.textSecondary)} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </span>
                        <p className={cn('text-xs font-medium truncate', clientSummary.kpis.overdueInvoicesCount > 0 ? 'text-red-700 dark:text-red-300' : colors.textSecondary)}>Overdue invoices</p>
                      </div>
                      <p className={cn('text-lg font-semibold mt-0.5', clientSummary.kpis.overdueInvoicesCount > 0 ? 'text-red-600 dark:text-red-400' : colors.text)}>
                        {clientSummary.kpis.overdueInvoicesCount}
                      </p>
                    </div>
                    {clientSummary.kpis.nextAppointmentDate && (
                      <div className={cn('rounded-xl border p-3 min-w-0', colors.border, colors.surface)}>
                        <div className="flex items-center gap-2">
                          <span className={cn('rounded-lg p-1.5', colors.surfaceHover)} aria-hidden>
                            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          </span>
                          <p className={cn('text-xs font-medium truncate', colors.textSecondary)}>Next appointment</p>
                        </div>
                        <p className={cn('text-sm font-semibold mt-0.5 truncate', colors.text)}>
                          {formatDate(clientSummary.kpis.nextAppointmentDate)}
                        </p>
                      </div>
                    )}
                    <div className={cn(
                      'rounded-xl border p-3 min-w-0',
                      clientSummary.kpis.servicesExpiringCount > 0
                        ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
                        : cn(colors.border, colors.surface)
                    )}>
                      <div className="flex items-center gap-2">
                        <span className={cn('rounded-lg p-1.5', clientSummary.kpis.servicesExpiringCount > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : colors.surfaceHover)} aria-hidden>
                          <svg className={cn('w-4 h-4', clientSummary.kpis.servicesExpiringCount > 0 ? 'text-amber-600 dark:text-amber-400' : colors.textSecondary)} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </span>
                        <p className={cn('text-xs font-medium truncate', clientSummary.kpis.servicesExpiringCount > 0 ? 'text-amber-700 dark:text-amber-300' : colors.textSecondary)}>Expiring in 30 days</p>
                      </div>
                      <p className={cn('text-lg font-semibold mt-0.5', clientSummary.kpis.servicesExpiringCount > 0 ? 'text-amber-600 dark:text-amber-400' : colors.text)}>
                        {clientSummary.kpis.servicesExpiringCount}
                      </p>
                    </div>
                  </>
                )}
                <div className={cn('rounded-xl border p-3 min-w-0', colors.border, colors.surface)}>
                  <div className="flex items-center gap-2">
                    <span className={cn('rounded-lg p-1.5', colors.surfaceHover)} aria-hidden>
                      <svg className="w-4 h-4 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                    </span>
                    <p className={cn('text-xs font-medium truncate', colors.textSecondary)}>Tasks</p>
                  </div>
                  <p className={cn('text-lg font-semibold mt-0.5', colors.text)}>{clientSummary._count?.clientTasks ?? 0}</p>
                </div>
                <div className={cn('rounded-xl border p-3 min-w-0', colors.border, colors.surface)}>
                  <div className="flex items-center gap-2">
                    <span className={cn('rounded-lg p-1.5', colors.surfaceHover)} aria-hidden>
                      <svg className="w-4 h-4 text-sky-600 dark:text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    </span>
                    <p className={cn('text-xs font-medium truncate', colors.textSecondary)}>Support tickets</p>
                  </div>
                  <p className={cn('text-lg font-semibold mt-0.5', colors.text)}>{clientSummary._count?.supportTickets ?? 0}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr,minmax(0,340px)] gap-6">
            {/* Left: accordion form */}
            <Accordion className="space-y-2">
              <AccordionItem title="📄 Basic information" defaultOpen={true}>
                <div className="space-y-0 pt-1">
                  <DetailRow label="Client name" value={clientSummary.clientName} />
                  <DetailRow label="Client name 2" value={clientSummary.clientName2} />
                  <DetailRow label="Client code" value={clientSummary.clientCode} />
                  <DetailRow label="Contact person" value={clientSummary.contactPerson} />
                </div>
              </AccordionItem>

              <AccordionItem title="☎️ Contact" defaultOpen={false}>
                <div className="space-y-0 pt-1">
                  <DetailRow label="Email" value={clientSummary.clientEmail} />
                  <DetailRow label="Email 2" value={clientSummary.clientEmail2} />
                  <DetailRow label="Phone" value={clientSummary.clientPhone} />
                  <DetailRow label="Phone 2" value={clientSummary.clientPhone2} />
                  <DetailRow label="Website" value={clientSummary.clientWebSite} />
                </div>
              </AccordionItem>

              <AccordionItem title="📍 Address" defaultOpen={false}>
                <div className="space-y-0 pt-1">
                  <DetailRow label="Address" value={clientSummary.clientAddress} />
                  <DetailRow label="City" value={clientSummary.clientCity} />
                  <DetailRow label="Zip code" value={clientSummary.clientZipCode} />
                  <DetailRow label="State" value={clientSummary.clientState} />
                  <DetailRow
                    label="Country"
                    value={clientSummary.clientCountryDescr?.pc_descr ?? undefined}
                  />
                </div>
              </AccordionItem>

              <AccordionItem title="💼 Business & tax" defaultOpen={false}>
                <div className="space-y-0 pt-1">
                  <DetailRow label="VAT number" value={clientSummary.clientVATNumber} />
                  <DetailRow label="GEMI number" value={clientSummary.gemiNumber} />
                  <DetailRow label="Source URL" value={clientSummary.sourceUrl} />
                  <DetailRow label="Payment terms (days)" value={clientSummary.clientPaymentTerms} />
                  <DetailRow
                    label="Tax authority"
                    value={clientSummary.clientTaxAuthorityDescr?.pt_descr ?? undefined}
                  />
                </div>
              </AccordionItem>

              <AccordionItem title="📋 Notes & status" defaultOpen={false}>
                <div className="space-y-0 pt-1">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {getStatusBadges(clientSummary)}
                  </div>
                  <DetailRow label="Notes" value={clientSummary.clientNotes} />
                  <DetailRow label="Date started" value={clientSummary.dateStarted ? formatDate(clientSummary.dateStarted) : undefined} />
                  <DetailRow label="Portal username" value={clientSummary.clientUserName} />
                  <DetailRow label="Created" value={clientSummary.clientCreatedAt ? formatDate(clientSummary.clientCreatedAt) : undefined} />
                  <DetailRow label="Updated" value={clientSummary.clientUpdatedAt ? formatDate(clientSummary.clientUpdatedAt) : undefined} />
                </div>
              </AccordionItem>
            </Accordion>

            {/* Right: jump to records */}
            <div className={cn('lg:sticky lg:top-0 self-start')}>
              <div className={cn('rounded-xl border p-4', colors.border, colors.surface)}>
                <h3 className={cn('text-sm font-semibold mb-1', colors.text)}>
                  Jump to records
                </h3>
                <p className={cn('text-xs mb-3', colors.textSecondary)}>
                  Click a link to scroll to that table below.
                </p>
                <RelatedModuleLinks
                  clientId={clientSummary.clientId}
                  counts={clientSummary._count}
                  singleColumn
                  onScrollToModule={handleScrollToModule}
                />
              </div>
            </div>
          </div>

      {/* Records tables at the end – one per module with count > 0 */}
      <div className="mt-8 pt-6">
        <h3 className={cn('text-sm font-semibold mb-1', colors.text)}>Records</h3>
        <p className={cn('text-xs mb-4', colors.textSecondary)}>Recent items per module. Use “Jump to records” above to scroll here.</p>
      </div>
      {RELATED_MODULES.filter(
        (config) => config.fetchUrl && (clientSummary._count?.[config.countKey] ?? 0) > 0
      ).map((config) => (
        <div
          key={config.key}
          id={`records-${config.key}`}
          className={cn('mt-8 scroll-mt-4 rounded-xl border overflow-hidden', colors.border, colors.surface)}
        >
          <div className={cn('flex items-center justify-between gap-3 px-4 py-3 border-b', colors.border, colors.surfaceHover)}>
            <h3 className={cn('text-base font-semibold tracking-tight', colors.text)}>
              {config.label}
            </h3>
            <Link
              to={config.path.startsWith('/client-vault') ? `${config.path}/${clientSummary.clientId}` : config.path}
              className={cn('text-sm font-medium shrink-0 rounded-md px-3 py-1.5 transition-colors', colors.textSecondary, 'hover:bg-black/5 dark:hover:bg-white/5 hover:underline')}
            >
              View full list →
            </Link>
          </div>
          {(() => {
                  const state = recordsByKey[config.key];
                  const count = clientSummary?._count?.[config.countKey] ?? 0;
                  if (!state) return null;
                  if (state.loading) {
                    return (
                      <div className="max-h-72 p-4">
                        <div className={cn('animate-pulse space-y-3', colors.surface)}>
                          {[1, 2, 3, 4].map((i) => (
                            <div key={i} className={cn('h-10 rounded-lg', colors.surfaceHover)} style={{ opacity: 0.7 }} />
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (state.error) {
                    return (
                      <div className={cn('px-4 py-4 text-sm', colors.textSecondary)}>
                        <p className="text-red-600 dark:text-red-400">{state.error}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => clientSummary?.clientId && loadModuleRecords(config, clientSummary.clientId)}
                        >
                          Retry
                        </Button>
                      </div>
                    );
                  }
                  if (state.data.length === 0) {
                    const countMismatch = count > 0;
                    return (
                      <div className={cn('px-4 py-4 text-sm', colors.textSecondary)}>
                        {countMismatch
                          ? 'No records shown. Count may differ due to filters or permissions.'
                          : 'No records.'}
                      </div>
                    );
                  }
                  const getId = config.getRecordId ?? (() => 0);
                  const getLabel = config.getRecordLabel ?? (() => '—');
                  const detailPath = config.detailPath;
                  const currentClientId = clientSummary?.clientId;
                  const pathFor = (recordId: number) => (detailPath ? detailPath(recordId, currentClientId) : '');
                  const utils = { formatDate, formatCurrency };
                  const listContext = { mutate };
                  const moduleColumns = config.getColumnsFromModule?.(utils, colors, listContext);

                  if (moduleColumns && moduleColumns.length > 0) {
                    return (
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead className={cn('sticky top-0 z-10', colors.surfaceHover)}>
                            <tr className={cn('border-b', colors.border)}>
                              {moduleColumns.map((col) => (
                                <th key={String(col.key)} className={cn('text-left text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap', colors.textSecondary)}>{col.header}</th>
                              ))}
                              <th className={cn('w-10 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider', colors.textSecondary)} aria-label="Actions" />
                            </tr>
                          </thead>
                          <tbody>
                            {state.data
                              .filter((record): record is Record<string, unknown> => record != null && typeof record === 'object')
                              .map((record: Record<string, unknown>, i: number) => {
                              const r = record;
                              const id = getId(r);
                              const viewPath = pathFor(id as number);
                              const editPath = viewPath && !viewPath.includes('?') ? `${viewPath}/edit` : viewPath;
                              return (
                                <tr
                                  key={id ?? i}
                                  className={cn('border-b last:border-b-0', colors.border, 'hover:bg-black/[0.03] dark:hover:bg-white/[0.03]')}
                                  role={detailPath && id != null ? 'button' : undefined}
                                  tabIndex={detailPath && id != null ? 0 : undefined}
                                  onClick={(e) => {
                                    if (detailPath && id != null && !(e.target as HTMLElement).closest('a, button')) {
                                      window.location.href = viewPath;
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (detailPath && id != null && (e.key === 'Enter' || e.key === ' ') && !(e.target as HTMLElement).closest('a, button')) {
                                      e.preventDefault();
                                      window.location.href = viewPath;
                                    }
                                  }}
                                >
                                  {moduleColumns.map((col) => (
                                    <td key={String(col.key)} className={cn('px-4 py-2.5', colors.text)}>
                                      {r != null && col.render
                                        ? col.render(getNestedValue(r, String(col.key)), r)
                                        : String(getNestedValue(r, String(col.key)) ?? '')}
                                    </td>
                                  ))}
                                  <td className="px-4 py-2.5 w-10 align-middle text-right">
                                    {detailPath && id != null && editPath && config.hasEdit !== false ? (
                                      <Link
                                        to={editPath}
                                        onClick={(e) => e.stopPropagation()}
                                        className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ml-auto', colors.textSecondary, 'hover:bg-black/10 dark:hover:bg-white/10')}
                                        title="Edit"
                                        aria-label="Edit"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </Link>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }

                  const cols = config.columns ?? [];
                  return (
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead className={cn('sticky top-0 z-10', colors.surfaceHover)}>
                          <tr className={cn('border-b', colors.border)}>
                            <th className={cn('text-left text-xs font-semibold uppercase tracking-wider px-4 py-3', colors.textSecondary)}>Title</th>
                            {cols.map((col) => (
                              <th key={col.key} className={cn('text-left text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap', colors.textSecondary)}>{col.label}</th>
                            ))}
                            <th className={cn('w-10 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider', colors.textSecondary)} aria-label="Actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {state.data
                            .filter((record): record is Record<string, unknown> => record != null && typeof record === 'object')
                            .map((record: Record<string, unknown>, i: number) => {
                            const r = record;
                            const id = getId(r);
                            const label = getLabel(r);
                            const viewPath = pathFor(id as number);
                            const editPath = viewPath && !viewPath.includes('?') ? `${viewPath}/edit` : viewPath;
                            return (
                              <tr key={id ?? i} className={cn('border-b last:border-b-0', colors.border, 'hover:bg-black/[0.03] dark:hover:bg-white/[0.03]')}>
                                <td className="px-4 py-2.5">
                                  {detailPath && id != null ? (
                                    <Link to={viewPath} className={cn('font-medium hover:underline', colors.text)}>
                                      {label}
                                    </Link>
                                  ) : (
                                    <span className={colors.text}>{label}</span>
                                  )}
                                </td>
                                {cols.map((col) => {
                                  const value = col.getValue(r);
                                  const statusColor = col.getStatusColor?.(r);
                                  const display = value === undefined || value === null || value === '' ? '' : String(value);
                                  return (
                                    <td key={col.key} className={cn('px-4 py-2.5 whitespace-nowrap', colors.textSecondary)}>
                                      {statusColor && display ? (
                                        <Badge color={statusColor}>{display}</Badge>
                                      ) : (
                                        display
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="px-4 py-2.5 w-10 align-middle text-right">
                                  {detailPath && id != null && editPath && config.hasEdit !== false ? (
                                    <Link
                                      to={editPath}
                                      className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ml-auto', colors.textSecondary, 'hover:bg-black/10 dark:hover:bg-white/10')}
                                      title="Edit"
                                      aria-label="Edit"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </Link>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
        </div>
      ))}
    </div>
  );
}

export const ClientSummaryModal: React.FC<ClientSummaryModalProps> = ({
  isOpen,
  onClose,
  clientId,
}) => {
  const { clientSummary, isLoading, mutate: mutateSummary } = useClientSummary(isOpen ? clientId : null);
  const { colors } = useTheme();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>
          {clientSummary?.clientName ?? (clientId ? `Client #${clientId}` : 'Client summary')}
        </ModalTitle>
        <div className="flex items-center gap-2">
          {clientSummary && (
            <button
              type="button"
              onClick={() => mutateSummary()}
              className="p-2 rounded hover:bg-black/10 dark:hover:bg-white/10"
              title="Refresh"
              aria-label="Refresh"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </ModalHeader>
      <ModalContent>
        {isLoading && (
          <div className={cn('py-8 text-center text-sm', colors.textSecondary)}>
            Loading client details…
          </div>
        )}
        {!isLoading && !clientSummary && clientId && (
          <div className={cn('py-8 text-center text-sm', colors.textSecondary)}>
            Client not found.
          </div>
        )}
        {!isLoading && clientSummary && (
          <ClientSummaryBody clientSummary={clientSummary} mutate={mutateSummary} />
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientSummaryModal;
